import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  AlertTriangle, AlertCircle, Info, CheckCircle2, Loader2, Bell, RefreshCw, Zap, Settings, Mail, Clock, TrendingDown, CreditCard, Palette, X,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  site_offline: { icon: AlertCircle, label: "Site hors ligne", color: "red" },
  revenue_drop: { icon: TrendingDown, label: "Chute de CA", color: "orange" },
  stripe_missing: { icon: CreditCard, label: "Stripe non configuré", color: "amber" },
  smtp_missing: { icon: Mail, label: "SMTP non configuré", color: "amber" },
  stale_theme: { icon: Palette, label: "Thème non défini", color: "slate" },
};

const SEVERITY_STYLES = {
  high: "border-red-300 bg-red-50 text-red-900",
  medium: "border-orange-300 bg-orange-50 text-orange-900",
  low: "border-amber-300 bg-amber-50 text-amber-900",
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
}

export default function AgencyAlertsPage() {
  const [tab, setTab] = useState("open");   // "open" | "resolved" | "settings"
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({ open_count: 0, high_open: 0 });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [resolveNote, setResolveNote] = useState("");

  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get(`/agency/alerts${tab === "resolved" ? "?status=resolved" : tab === "open" ? "?status=open" : ""}`),
        api.get("/settings"),
      ]);
      setAlerts(a.data.alerts || []);
      setCounts({ open_count: a.data.open_count || 0, high_open: a.data.high_open || 0 });
      setSettings(s.data);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/agency/alerts/run-now");
      if (data.ok) toast.success(`Évaluation : ${data.alerts_triggered} nouvelles alertes · ${data.emails_sent} emails envoyés`);
      else toast.warning(data.error || "Alertes désactivées");
      await load();
    } catch { toast.error("Erreur"); }
    finally { setRunning(false); }
  };

  const resolve = async (id) => {
    try {
      await api.post(`/agency/alerts/${id}/resolve`, { note: resolveNote });
      toast.success("Alerte résolue");
      setResolving(null); setResolveNote("");
      await load();
    } catch { toast.error("Erreur"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement ?")) return;
    await api.delete(`/agency/alerts/${id}`);
    load();
  };

  const saveSettings = async (patch) => {
    setSaving(true);
    try {
      await api.put("/settings", patch);
      toast.success("Paramètres enregistrés");
      load();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-10 space-y-5" data-testid="agency-alerts-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)] flex items-center gap-2">
            <Bell className="w-7 h-7 text-[color:var(--brand-sky)]"/>Alertes
            {counts.open_count > 0 && (
              <Badge className="bg-red-600 text-white border-0 ml-2" data-testid="open-count">{counts.open_count}</Badge>
            )}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Surveillance proactive de vos sites clients : pannes, chutes de CA, configs manquantes.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runNow} disabled={running} data-testid="run-now-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Zap className="w-4 h-4 mr-2"/>}
            Évaluer maintenant
          </Button>
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4"/></Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-[hsl(var(--muted))] rounded-lg w-fit">
        {[
          { k: "open", l: `Actives (${counts.open_count})` },
          { k: "resolved", l: "Résolues" },
          { k: "settings", l: "Paramètres" },
        ].map(t => (
          <button key={t.k} type="button" onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 text-sm rounded-md transition ${tab === t.k ? "bg-white shadow-sm font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}
            data-testid={`tab-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "settings" ? (
        <Card className="p-5" data-testid="alerts-settings">
          {settings === null ? <Loader2 className="w-5 h-5 animate-spin mx-auto"/> : (
            <div className="space-y-4 max-w-2xl">
              <label className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label className="font-semibold">Alertes activées</Label>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Désactive complètement le système de surveillance.</p>
                </div>
                <Switch checked={settings.hub_alerts_enabled !== false} onCheckedChange={v => saveSettings({ hub_alerts_enabled: v })} data-testid="toggle-alerts"/>
              </label>
              <div>
                <Label className="text-sm">Email destinataire des alertes</Label>
                <Input type="email" value={settings.hub_alert_email || ""} onChange={e => setSettings({...settings, hub_alert_email: e.target.value})} onBlur={e => saveSettings({ hub_alert_email: e.target.value })} placeholder="alerts@agence-msc.fr" data-testid="alert-email"/>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Fallback : agency_email si vide.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Seuil offline (minutes)</Label>
                  <Input type="number" min="30" value={settings.hub_alert_offline_min || 180} onChange={e => setSettings({...settings, hub_alert_offline_min: parseInt(e.target.value) || 180})} onBlur={e => saveSettings({ hub_alert_offline_min: parseInt(e.target.value) || 180 })} data-testid="alert-offline-min"/>
                </div>
                <div>
                  <Label className="text-sm">Seuil chute CA (%)</Label>
                  <Input type="number" min="10" max="90" value={settings.hub_alert_revenue_drop_pct || 40} onChange={e => setSettings({...settings, hub_alert_revenue_drop_pct: parseFloat(e.target.value) || 40})} onBlur={e => saveSettings({ hub_alert_revenue_drop_pct: parseFloat(e.target.value) || 40 })} data-testid="alert-drop-pct"/>
                </div>
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] p-3 bg-[hsl(var(--muted))]/40 rounded">
                <strong>Évaluation :</strong> toutes les heures en arrière-plan. Vous pouvez forcer une exécution immédiate via le bouton "Évaluer maintenant" ci-dessus.
              </div>
            </div>
          )}
        </Card>
      ) : loading ? (
        <Card className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[color:var(--brand-sky)]"/></Card>
      ) : alerts.length === 0 ? (
        <Card className="p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="alerts-empty">
          <CheckCircle2 className="w-10 h-10 mx-auto opacity-30 mb-3"/>
          <p className="font-semibold">{tab === "open" ? "Tout est en ordre" : "Aucune alerte résolue"}</p>
          <p className="text-sm mt-1">{tab === "open" ? "Aucune alerte active sur vos sites clients." : "Les alertes résolues apparaîtront ici."}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map(a => {
            const meta = TYPE_META[a.type] || { icon: Info, label: a.type, color: "slate" };
            const Icon = meta.icon;
            const severity = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.low;
            return (
              <Card key={a.id} className={`p-4 border-l-4 ${severity}`} data-testid={`alert-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold">{meta.label}</span>
                      <Badge variant="outline" className="text-xs bg-white">{a.severity}</Badge>
                      {a.notified && <Badge variant="outline" className="text-xs bg-white"><Mail className="w-3 h-3 mr-1"/>Email envoyé</Badge>}
                      {a.resolved_at && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs">Résolue</Badge>}
                    </div>
                    <div className="text-sm mt-1 font-semibold truncate">{a.company_name || "Site sans nom"}</div>
                    <div className="text-xs opacity-80 mt-1">{a.message}</div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] opacity-70">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3"/>Déclenchée {fmtDate(a.triggered_at)}</span>
                      {a.resolved_at && <span>Résolue {fmtDate(a.resolved_at)}</span>}
                      {a.site_url && <a href={a.site_url} target="_blank" rel="noopener" className="underline">Ouvrir le site</a>}
                    </div>
                    {resolving === a.id && (
                      <div className="mt-3 space-y-2">
                        <Textarea value={resolveNote} onChange={e => setResolveNote(e.target.value)} placeholder="Note de résolution (optionnel)" rows={2} className="bg-white"/>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => resolve(a.id)} data-testid={`confirm-resolve-${a.id}`}>Confirmer la résolution</Button>
                          <Button size="sm" variant="outline" onClick={() => { setResolving(null); setResolveNote(""); }}>Annuler</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  {!a.resolved_at && resolving !== a.id && (
                    <Button size="sm" variant="outline" className="bg-white" onClick={() => setResolving(a.id)} data-testid={`resolve-${a.id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1"/>Résoudre
                    </Button>
                  )}
                  <button onClick={() => del(a.id)} className="p-1 hover:bg-white/50 rounded" title="Supprimer" data-testid={`delete-${a.id}`}>
                    <X className="w-4 h-4"/>
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
